import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Hidden from "@insite/mobius/Hidden";
import Link from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Facebook from "@ucs/Icons/Facebook";
import LinkedIn from "@ucs/Icons/LinkedIn";
import Twitter from "@ucs/Icons/Twitter";
import UCSColors from "@ucs/Utils/UCSColors";
import React from "react";
import styled, { css } from "styled-components";

const enum fields {
    date = "date",
    shareLabel = "shareLabel",
    facebook = "facebook",
    twitter = "twitter",
    linkedin = "linkedin",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.date]: Date;
        [fields.shareLabel]: string;
        [fields.facebook]: boolean;
        [fields.twitter]: boolean;
        [fields.linkedin]: boolean;
    };
}

type Props = OwnProps;

export interface NewsDateAndShareStyles {
    wrapper?: InjectableCss;
    social?: InjectableCss;
    dateLabel?: TypographyProps;
    shareLabel?: TypographyProps;
    linksWrapper?: InjectableCss;
    linkStyle?: InjectableCss;
}

export const newsDateAndShareStyles: NewsDateAndShareStyles = {
    wrapper: {
        css: css`
            min-width: 95px;
            width: fit-content;
            margin-right: 95px;
            text-align: center;
            padding-bottom: 16px;
            border-bottom: 2px solid ${UCSColors.gray07};
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                        display: flex;
                        align-items: center;
                        margin-right: 0;
                        border-bottom: none;
                    `,
                    css`
                        margin-right: 35px;
                    `,
                    css`
                        margin-right: 65px;
                    `,
                ]);
            }}
        `,
    },
    social: {
        css: css``,
    },
    dateLabel: {
        variant: "p",
        size: 13,
        weight: 800,
        color: UCSColors.gray03,
        css: css`
            margin-bottom: 0;
            padding: 16px 0 0;
            border-top: 2px solid ${UCSColors.gray07};
            line-height: 16px;
            letter-spacing: 0.1em;
            white-space: nowrap;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        border-top: none;
                        padding: 16px;
                    `,
                ]);
            }}
        `,
    },
    shareLabel: {
        variant: "p",
        size: 13,
        transform: "uppercase",
        weight: 800,
        color: UCSColors.gray03,
        css: css`
            margin-bottom: 0;
            padding: 16px 2px;
            border-top: 2px solid ${UCSColors.gray07};
            line-height: 16px;
            letter-spacing: 0.1em;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        border-top: none;
                        padding: 16px;
                    `,
                ]);
            }}
        `,
    },
    linksWrapper: {
        css: css`
            width: 95px;
            padding: 0 0;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            & > * {
                &:not(:first-child) {
                    margin-top: 16px;
                }
            }
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                        flex-direction: row;
                        & > * {
                            &:not(:first-child) {
                                margin-top: 0;
                                margin-left: 16px;
                            }
                        }
                    `,
                ]);
            }}
        `,
    },
    linkStyle: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            width: 32px;
            height: 32px;
            border-radius: 50px;
            background-color: ${UCSColors.gray03};

            &:hover {
                background-color: ${UCSColors.gray04};
                color: ${UCSColors.gray10} !important;
            }
        `,
    },
};

const styles = newsDateAndShareStyles;

const Separator = styled.div`
    min-height: 34px;
    border-right: 2px solid ${UCSColors.gray07};
    display: flex;
`;

const NewsDateAndShare: React.FC<Props> = ({ id, fields, ...props }) => {
    const { shareLabel, date, facebook, twitter, linkedin } = fields;

    // const pageLink = typeof window !== "undefined" ? window.location.href : "";

    const getIconProps = (icon: any) => ({
        iconProps: {
            color: `${UCSColors.gray10}`,
            src: icon,
            size: 20,
            css: css`
                margin-right: 0;
                &:hover {
                    color: ${UCSColors.gray10} !important;
                }
            `,
        },
    });

    const getDateString = () => {
        if (!date) {
            return "";
        }
        const newDate = new Date(date);
        const day = `0${newDate.getDate()}`.slice(-2);
        const month = new Intl.DateTimeFormat("en-US", { month: "short" }).format(newDate);
        const year = newDate.getFullYear();
        return `${month} ${+day < 10 ? day.slice(1, 2) : day}, ${year}`;
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <Hidden above="xs">
                <Separator />
            </Hidden>
            <Typography {...styles.dateLabel}>{getDateString()}</Typography>
            <Hidden above="xs">
                <Separator />
            </Hidden>

            {/* {shareLabel && <Typography {...styles.shareLabel}>{shareLabel}</Typography>}

            <StyledWrapper {...styles.linksWrapper}>
                {twitter && (
                    <Link
                        href="https://twitter.com/UpperCanada_FP"
                        target="_blank"
                        icon={getIconProps(Twitter)}
                        {...styles.linkStyle}
                        hoverStyle={{ color: `${UCSColors.gray10} !important` }}
                    ></Link>
                )}
                {facebook && (
                    <Link
                        href="https://www.facebook.com/uppercanadaforestproducts"
                        target="_blank"
                        icon={getIconProps(Facebook)}
                        {...styles.linkStyle}
                        hoverStyle={{ color: `${UCSColors.gray10} !important` }}
                    ></Link>
                )}
                {linkedin && (
                    <Link
                        href="https://www.linkedin.com/company/uppercanadaforestproducts/"
                        target="_blank"
                        icon={getIconProps(LinkedIn)}
                        {...styles.linkStyle}
                        hoverStyle={{ color: `${UCSColors.gray10} !important` }}
                    ></Link>
                )}
            </StyledWrapper>
            <Hidden above="xs">
                <Separator style={{ paddingLeft: 16 }} />
            </Hidden> */}
        </StyledWrapper>
    );
};

const definition: WidgetDefinition = {
    group: "Common",
    displayName: "News Date & Share",
    fieldDefinitions: [
        {
            name: fields.date,
            editorTemplate: "DateTimeField",
            defaultValue: null,
            fieldType: "General",
            isRequired: true,
        },
        {
            name: fields.shareLabel,
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "Translatable",
            isRequired: false,
        },
        {
            name: fields.twitter,
            displayName: "Twitter share",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "General",
        },
        {
            name: fields.facebook,
            displayName: "Facebook share",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "General",
        },
        {
            name: fields.linkedin,
            displayName: "LinkedIn share",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "General",
        },
    ],
};

const widgetModule: WidgetModule = {
    component: NewsDateAndShare,
    definition,
};

export default widgetModule;
